'use client';

import React, { useState, useEffect } from 'react';

import PlatformNav from '@/components/PlatformNav';
import Icon from '@/components/ui/AppIcon';


interface VisaService {
  id: string;
  country_code: string;
  country_name: string;
  visa_type: string;
  title: string;
  description: string;
  processing_time_days: number;
  validity_days: number;
  max_stay_days: number;
  entries: string;
  fee_amount: number;
  fee_currency: string;
  requirements: string[];
  documents_required: string[];
  is_featured: boolean;
}

const VISA_TYPES = [
  { value: '', label: 'All Types' },
  { value: 'tourist', label: 'Tourist' },
  { value: 'business', label: 'Business' },
  { value: 'e_visa', label: 'e-Visa' },
  { value: 'visa_on_arrival', label: 'Visa on Arrival' },
  { value: 'transit', label: 'Transit' },
  { value: 'multiple_entry', label: 'Multiple Entry' },
];

const COUNTRY_FLAGS: Record<string, string> = {
  AE: '🇦🇪', IN: '🇮🇳', TH: '🇹🇭', US: '🇺🇸', GB: '🇬🇧',
  FR: '🇫🇷', DE: '🇩🇪', JP: '🇯🇵', AU: '🇦🇺', SG: '🇸🇬',
  IT: '🇮🇹', ES: '🇪🇸', TR: '🇹🇷', ID: '🇮🇩', MY: '🇲🇾',
};

export default function VisaServicesPage() {
  const [visaServices, setVisaServices] = useState<VisaService[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedType, setSelectedType] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedVisa, setSelectedVisa] = useState<VisaService | null>(null);
  const [showLeadForm, setShowLeadForm] = useState(false);
  const [leadForm, setLeadForm] = useState({
    contact_name: '', contact_email: '', contact_phone: '',
    destination: '', travel_date: '', travelers_count: '1', requirements: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    fetchVisaServices();
  }, [selectedType]);

  async function fetchVisaServices() {
    setLoading(true);
    try {
      const params = new URLSearchParams({ limit: '20' });
      if (selectedType) params.set('visa_type', selectedType);
      const res = await fetch(`/api/visa-services?${params}`);
      const json = await res.json();
      setVisaServices(json.data || []);
    } catch {
      setVisaServices([]);
    } finally {
      setLoading(false);
    }
  }

  const filtered = visaServices.filter((v) =>
    !searchQuery ||
    v.country_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    v.title.toLowerCase().includes(searchQuery.toLowerCase())
  );

  async function handleLeadSubmit(e: React.FormEvent) {
    e.preventDefault();
    setSubmitting(true);
    try {
      await fetch('/api/service-leads', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...leadForm,
          service_type: 'visa',
          service_id: selectedVisa?.id,
          destination: selectedVisa?.country_name || leadForm.destination,
          source: 'visa_page',
        }),
      });
      setSubmitted(true);
    } catch {
      setSubmitted(true);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="min-h-screen" style={{ background: 'var(--color-background)' }}>
      <PlatformNav />
      <div className="pt-16">
        {/* Hero */}
        <section className="relative py-20 px-6 overflow-hidden">
          <div className="absolute inset-0 opacity-10"
            style={{ background: 'radial-gradient(ellipse at 30% 50%, var(--color-primary) 0%, transparent 60%)' }} />
          <div className="max-w-5xl mx-auto relative">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-xl flex items-center justify-center"
                style={{ background: 'var(--color-primary-10)' }}>
                <Icon name="DocumentTextIcon" size={20} className="text-primary" />
              </div>
              <span className="label-caps text-primary">Visa Services</span>
            </div>
            <h1 className="font-serif text-4xl md:text-5xl font-semibold text-foreground mb-4">
              Seamless Visa Assistance
            </h1>
            <p className="text-lg text-muted-foreground max-w-2xl mb-8">
              Expert visa guidance for 190+ countries. Our specialists handle every detail — from documentation to submission — so your journey begins without friction.
            </p>
            <div className="flex flex-wrap gap-6 text-sm text-muted-foreground">
              {[
                { icon: 'ClockIcon', label: '48-hour processing for most e-Visas' },
                { icon: 'ShieldCheckIcon', label: '99.2% approval rate' },
                { icon: 'GlobeAltIcon', label: '190+ countries covered' },
                { icon: 'UserGroupIcon', label: 'Dedicated visa specialists' },
              ].map((item) => (
                <div key={item.label} className="flex items-center gap-2">
                  <Icon name={item.icon as Parameters<typeof Icon>[0]['name']} size={16} className="text-primary" />
                  <span>{item.label}</span>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Filters */}
        <section className="px-6 pb-8">
          <div className="max-w-5xl mx-auto">
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="relative flex-1">
                <Icon name="MagnifyingGlassIcon" size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <input
                  type="text"
                  placeholder="Search by country or visa type..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-9 pr-4 py-2.5 rounded-lg text-sm border border-border bg-surface-2 text-foreground placeholder:text-muted-foreground focus:outline-none focus:border-primary"
                />
              </div>
              <div className="flex gap-2 flex-wrap">
                {VISA_TYPES.map((type) => (
                  <button
                    key={type.value}
                    onClick={() => setSelectedType(type.value)}
                    className={`px-3 py-2 rounded-lg text-xs font-medium transition-all ${
                      selectedType === type.value
                        ? 'bg-primary text-white' :'bg-surface-2 text-muted-foreground hover:text-foreground border border-border'
                    }`}
                  >
                    {type.label}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Visa Cards */}
        <section className="px-6 pb-16">
          <div className="max-w-5xl mx-auto">
            {loading ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {[...Array(4)].map((_, i) => (
                  <div key={i} className="h-48 rounded-xl bg-surface-2 animate-pulse" />
                ))}
              </div>
            ) : filtered.length === 0 ? (
              <div className="text-center py-16">
                <Icon name="DocumentTextIcon" size={40} className="text-muted-foreground mx-auto mb-4" />
                <p className="text-muted-foreground">No visa services found. Contact our specialists for assistance.</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {filtered.map((visa) => (
                  <div
                    key={visa.id}
                    className="rounded-xl border border-border bg-surface-2 p-5 hover:border-primary/40 transition-all cursor-pointer group"
                    onClick={() => setSelectedVisa(visa)}
                  >
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-center gap-3">
                        <span className="text-3xl">{COUNTRY_FLAGS[visa.country_code] || '🌍'}</span>
                        <div>
                          <h3 className="font-semibold text-foreground group-hover:text-primary transition-colors">
                            {visa.title}
                          </h3>
                          <p className="text-xs text-muted-foreground">{visa.country_name}</p>
                        </div>
                      </div>
                      {visa.is_featured && (
                        <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-primary/10 text-primary">
                          Popular
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-muted-foreground mb-4 line-clamp-2">{visa.description}</p>
                    <div className="grid grid-cols-3 gap-3 mb-4">
                      <div className="text-center p-2 rounded-lg bg-surface-3">
                        <p className="text-xs text-muted-foreground">Processing</p>
                        <p className="text-sm font-semibold text-foreground">{visa.processing_time_days}d</p>
                      </div>
                      <div className="text-center p-2 rounded-lg bg-surface-3">
                        <p className="text-xs text-muted-foreground">Validity</p>
                        <p className="text-sm font-semibold text-foreground">{visa.validity_days}d</p>
                      </div>
                      <div className="text-center p-2 rounded-lg bg-surface-3">
                        <p className="text-xs text-muted-foreground">Fee</p>
                        <p className="text-sm font-semibold text-foreground">
                          {visa.fee_amount ? `${visa.fee_currency} ${visa.fee_amount}` : 'Free'}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="px-2 py-0.5 rounded-full text-xs bg-surface-3 text-muted-foreground capitalize">
                        {visa.visa_type?.replace(/_/g, ' ')}
                      </span>
                      <button
                        onClick={(e) => { e.stopPropagation(); setSelectedVisa(visa); setShowLeadForm(true); }}
                        className="text-xs font-medium text-primary hover:underline"
                      >
                        Apply Now →
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </section>

        {/* CTA */}
        <section className="px-6 pb-16">
          <div className="max-w-5xl mx-auto">
            <div className="rounded-2xl border border-primary/20 p-8 text-center"
              style={{ background: 'linear-gradient(135deg, var(--color-primary-10) 0%, transparent 100%)' }}>
              <h2 className="font-serif text-2xl font-semibold text-foreground mb-3">
                Need a visa not listed here?
              </h2>
              <p className="text-muted-foreground mb-6">
                Our visa specialists cover 190+ countries. Contact us for any destination.
              </p>
              <button
                onClick={() => { setSelectedVisa(null); setShowLeadForm(true); }}
                className="px-6 py-3 rounded-xl font-medium text-white transition-all"
                style={{ background: 'var(--color-primary)' }}
              >
                Speak to a Visa Specialist
              </button>
            </div>
          </div>
        </section>
      </div>

      {/* Visa Detail Modal */}
      {selectedVisa && !showLeadForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" onClick={() => setSelectedVisa(null)}>
          <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" />
          <div
            className="relative w-full max-w-lg rounded-2xl border border-border bg-surface-2 p-6 max-h-[85vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <button onClick={() => setSelectedVisa(null)} className="absolute top-4 right-4 p-1.5 rounded-lg hover:bg-surface-3 text-muted-foreground">
              <Icon name="XMarkIcon" size={18} />
            </button>
            <div className="flex items-center gap-3 mb-4">
              <span className="text-4xl">{COUNTRY_FLAGS[selectedVisa.country_code] || '🌍'}</span>
              <div>
                <h2 className="font-serif text-xl font-semibold text-foreground">{selectedVisa.title}</h2>
                <p className="text-sm text-muted-foreground">{selectedVisa.country_name}</p>
              </div>
            </div>
            <p className="text-sm text-muted-foreground mb-5">{selectedVisa.description}</p>
            <div className="grid grid-cols-2 gap-3 mb-5">
              {[
                { label: 'Processing Time', value: `${selectedVisa.processing_time_days} business days` },
                { label: 'Visa Validity', value: `${selectedVisa.validity_days} days` },
                { label: 'Max Stay', value: `${selectedVisa.max_stay_days} days` },
                { label: 'Entry Type', value: selectedVisa.entries },
                { label: 'Visa Fee', value: selectedVisa.fee_amount ? `${selectedVisa.fee_currency} ${selectedVisa.fee_amount}` : 'Free' },
                { label: 'Visa Type', value: selectedVisa.visa_type?.replace(/_/g, ' ') },
              ].map((item) => (
                <div key={item.label} className="p-3 rounded-lg bg-surface-3">
                  <p className="text-xs text-muted-foreground mb-1">{item.label}</p>
                  <p className="text-sm font-medium text-foreground capitalize">{item.value}</p>
                </div>
              ))}
            </div>
            {selectedVisa.requirements?.length > 0 && (
              <div className="mb-4">
                <h3 className="text-sm font-semibold text-foreground mb-2">Requirements</h3>
                <ul className="space-y-1">
                  {selectedVisa.requirements.map((req, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm text-muted-foreground">
                      <Icon name="CheckCircleIcon" size={14} className="text-primary mt-0.5 shrink-0" />
                      {req}
                    </li>
                  ))}
                </ul>
              </div>
            )}
            <button
              onClick={() => setShowLeadForm(true)}
              className="w-full py-3 rounded-xl font-medium text-white transition-all"
              style={{ background: 'var(--color-primary)' }}
            >
              Apply for this Visa
            </button>
          </div>
        </div>
      )}

      {/* Lead Form Modal */}
      {showLeadForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" onClick={() => { setShowLeadForm(false); setSubmitted(false); }}>
          <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" />
          <div
            className="relative w-full max-w-md rounded-2xl border border-border bg-surface-2 p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <button onClick={() => { setShowLeadForm(false); setSubmitted(false); }} className="absolute top-4 right-4 p-1.5 rounded-lg hover:bg-surface-3 text-muted-foreground">
              <Icon name="XMarkIcon" size={18} />
            </button>
            {submitted ? (
              <div className="text-center py-8">
                <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4" style={{ background: 'var(--color-primary-10)' }}>
                  <Icon name="CheckCircleIcon" size={32} className="text-primary" />
                </div>
                <h3 className="font-serif text-xl font-semibold text-foreground mb-2">Enquiry Submitted</h3>
                <p className="text-muted-foreground text-sm">Our visa specialist will contact you within 2 business hours.</p>
              </div>
            ) : (
              <>
                <h2 className="font-serif text-xl font-semibold text-foreground mb-1">
                  {selectedVisa ? `Apply: ${selectedVisa.title}` : 'Visa Enquiry'}
                </h2>
                <p className="text-sm text-muted-foreground mb-5">Our specialist will guide you through the process.</p>
                <form onSubmit={handleLeadSubmit} className="space-y-3">
                  <input
                    type="text" placeholder="Full Name *" required
                    value={leadForm.contact_name}
                    onChange={(e) => setLeadForm({ ...leadForm, contact_name: e.target.value })}
                    className="w-full px-3 py-2.5 rounded-lg text-sm border border-border bg-surface-3 text-foreground placeholder:text-muted-foreground focus:outline-none focus:border-primary"
                  />
                  <input
                    type="email" placeholder="Email Address *" required
                    value={leadForm.contact_email}
                    onChange={(e) => setLeadForm({ ...leadForm, contact_email: e.target.value })}
                    className="w-full px-3 py-2.5 rounded-lg text-sm border border-border bg-surface-3 text-foreground placeholder:text-muted-foreground focus:outline-none focus:border-primary"
                  />
                  <input
                    type="tel" placeholder="Phone Number"
                    value={leadForm.contact_phone}
                    onChange={(e) => setLeadForm({ ...leadForm, contact_phone: e.target.value })}
                    className="w-full px-3 py-2.5 rounded-lg text-sm border border-border bg-surface-3 text-foreground placeholder:text-muted-foreground focus:outline-none focus:border-primary"
                  />
                  <input
                    type="date" placeholder="Travel Date"
                    value={leadForm.travel_date}
                    onChange={(e) => setLeadForm({ ...leadForm, travel_date: e.target.value })}
                    className="w-full px-3 py-2.5 rounded-lg text-sm border border-border bg-surface-3 text-foreground focus:outline-none focus:border-primary"
                  />
                  <input
                    type="number" placeholder="Number of Travelers" min="1"
                    value={leadForm.travelers_count}
                    onChange={(e) => setLeadForm({ ...leadForm, travelers_count: e.target.value })}
                    className="w-full px-3 py-2.5 rounded-lg text-sm border border-border bg-surface-3 text-foreground placeholder:text-muted-foreground focus:outline-none focus:border-primary"
                  />
                  <textarea
                    placeholder="Special requirements or questions..."
                    rows={3}
                    value={leadForm.requirements}
                    onChange={(e) => setLeadForm({ ...leadForm, requirements: e.target.value })}
                    className="w-full px-3 py-2.5 rounded-lg text-sm border border-border bg-surface-3 text-foreground placeholder:text-muted-foreground focus:outline-none focus:border-primary resize-none"
                  />
                  <button
                    type="submit" disabled={submitting}
                    className="w-full py-3 rounded-xl font-medium text-white transition-all disabled:opacity-50"
                    style={{ background: 'var(--color-primary)' }}
                  >
                    {submitting ? 'Submitting...' : 'Submit Enquiry'}
                  </button>
                </form>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
